import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { BadgeApiInfo } from '@shared/types/badge.type';
import { MyProfileService } from '@src/app/modules/my-profile/my-profile.service';
import { CommonService } from '@src/app/services/common.service';
import { WalletStatusService } from '@src/app/services/wallet-status.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { tadaAnimation } from 'angular-animations';
import dayjs from 'dayjs';
import tz from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzSkeletonModule } from 'ng-zorro-antd/skeleton';
import { BehaviorSubject, Subject, combineLatest, filter, takeUntil } from 'rxjs';
import { CheckInWeekService, CheckWeekInfo, RetroactiveCardInfo } from './check-in-week.service';
dayjs.extend(utc);
dayjs.extend(tz);

@Component({
    selector: 'app-check-in-week',
    standalone: true,
    imports: [CommonModule, SharedModule, NzModalModule, NzSkeletonModule],
    templateUrl: './check-in-week.component.html',
    styleUrls: ['./check-in-week.component.less'],
    providers: [CheckInWeekService],
    animations: [tadaAnimation()],
})
export class CheckInWeekComponent implements OnInit, OnDestroy {
    dailyGm: {
        name: string;
        points: number;
        isChecked: boolean;
        isMissing?: boolean;
        badge?: BadgeApiInfo;
    }[] = [
        {
            name: 'Day 1',
            points: 10,
            isChecked: false,
            isMissing: false,
        },
        {
            name: 'Day 2',
            points: 30,
            isChecked: false,
            isMissing: false,
        },
        {
            name: 'Day 3',
            points: 40,
            isChecked: false,
        },
        {
            name: 'Day 4',
            points: 40,
            isChecked: false,
        },
        {
            name: 'Day 5',
            points: 40,
            isChecked: false,
        },
        {
            name: 'Day 6',
            points: 60,
            isChecked: false,
        },
        {
            name: 'Day 7',
            points: 100,
            isChecked: false,
        },
    ];

    checkWeekInfo$ = new BehaviorSubject<CheckWeekInfo>(null);

    animationState = false;

    gmRulesVisible = false;

    submitting = false;

    fetchingData = false;

    get todayChecked() {
        return this.checkWeekInfo$.getValue()?.login_today;
    }

    get backgroundStr() {
        const loginStreak = this.checkWeekInfo$.getValue()?.login_streak;
        if (loginStreak < 7) {
            return `linear-gradient(90deg, #000 0%, #000 ${((loginStreak - 1) / 6) * 100}%)`;
        } else {
            return '#000';
        }
    }

    get loginBreak() {
        return this.dailyGm.some(item => item.isMissing);
    }

    get isStartNewStreakMode() {
        return (this.loginBreak || this.checkWeekInfo$.getValue()?.login_streak === 7) && !this.hasRetroactiveCard;
    }

    get hasRetroactiveCard() {
        return this.retroactiveCardInfo?.data?.some(item => !item.used_at);
    }

    destroy$ = new Subject<void>();

    intervalId: any;

    refreshTime = '00:00';
    userTz = dayjs.tz.guess();

    retroactiveCardInfo: RetroactiveCardInfo;

    constructor(
        private myProfileService: MyProfileService,
        private walletStatusService: WalletStatusService,
        private checkInWeekService: CheckInWeekService,
        public commonService: CommonService
    ) {}

    ngOnInit() {
        this.refreshTime = dayjs().utc().startOf('day').tz(dayjs.tz.guess()).format('HH:mm');

        this.fetchingData = true;
        this.checkInWeekService
            .getCheckInWeekInfo()
            .then(data => this.checkWeekInfo$.next(data))
            .finally(() => (this.fetchingData = false));

        this.checkInWeekService.retroactiveCardInfo().then(data => (this.retroactiveCardInfo = data));

        combineLatest([this.myProfileService.badges$, this.checkWeekInfo$])
            .pipe(
                filter(item => !!item[0] && !!item[1]),
                takeUntil(this.destroy$)
            )
            .subscribe(([badges, checkWeekInfo]) => {
                const twoDayBadge = badges.find(badge => badge.id === '2nd_day_login');
                const sevenDayBadge = badges.find(badge => badge.id === '7_day_login');
                twoDayBadge.earned_at = null;
                sevenDayBadge.earned_at = null;

                const isTwoBadgeInThisRound = dayjs(twoDayBadge.earned_at).isSame(checkWeekInfo.login_records?.[1]?.created_at, 'day');
                const isSevenBadgeInThisRound = dayjs(sevenDayBadge.earned_at).isSame(checkWeekInfo.login_records?.[6]?.created_at, 'day');

                const twoDayBadgeVisible = !twoDayBadge.earned_at || isTwoBadgeInThisRound;
                const sevenDayBadgeVisible = !sevenDayBadge?.earned_at || isSevenBadgeInThisRound;

                if (twoDayBadgeVisible) {
                    this.dailyGm[1].badge = twoDayBadge;
                }
                if (sevenDayBadgeVisible) {
                    this.dailyGm[6].badge = sevenDayBadge;
                }

                for (let i = 0; i < checkWeekInfo.login_streak; i++) {
                    this.dailyGm[i].isChecked = true;
                }

                for (let i = checkWeekInfo.login_streak; i < this.dailyGm.length; i++) {
                    this.dailyGm[i].isChecked = false;
                }

                const lastCheckInDate = dayjs(checkWeekInfo.login_records?.[checkWeekInfo.login_records?.length - 1]?.created_at);
                const isNextCheckInMissing = lastCheckInDate.isBefore(dayjs().add(-1, 'day').startOf('day'));

                if (isNextCheckInMissing && checkWeekInfo.login_records?.length < 7) {
                    this.dailyGm[checkWeekInfo.login_records?.length].isMissing = true;
                } else {
                    this.dailyGm.forEach(item => (item.isMissing = false));
                }

                if (!this.intervalId && !checkWeekInfo.login_today) {
                    this.intervalId = setInterval(() => {
                        this.animationState = !this.animationState;
                    }, 3000);
                }
            });
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    checkIn() {
        this.submitting = true;
        this.checkInWeekService
            .checkIn()
            .then(data => {
                clearInterval(this.intervalId);
                this.intervalId = null;

                this.checkWeekInfo$.next(data);

                let earnedPoints = this.dailyGm[data.login_streak - 1].points;
                if (data.earned_badges?.length) {
                    this.commonService.showGotNewBadgesModal(data.earned_badges, earnedPoints);
                    earnedPoints += data.earned_badges.reduce((acc, badge) => acc + badge.points, 0);
                } else {
                    this.commonService.success(`Congrats! You've gain ${earnedPoints} points.`);
                }

                this.myProfileService.pullNewUserProfile();
            })
            .catch(err => this.commonService.errorMessageByResponse(err))
            .finally(() => (this.submitting = false));
    }
}
